import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Sidebar.css";
import { ExternalLink, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Sidebar({ refreshKey }) {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const [profile, setProfile] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const navi = useNavigate();

  const MAX_POSTS = 50;
  const MAX_CONNECTIONS = 50;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/profile/${storedUser.userid}`
        );
        const weeklyRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/activity/weekly`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setProfile(profileRes.data);
        setWeekly(weeklyRes.data);
      } catch (error) {
        console.error("Sidebar error:", error);
      }
    };
    if (storedUser?.userid) fetchData();
  }, [refreshKey, storedUser?.userid, token]);

  if (!profile || !weekly) return null;

  // Calculate percentages based on max thresholds of 50
  const postsPercentage = Math.min((weekly.weeklyPosts / MAX_POSTS) * 100, 100);
  const connectionsPercentage = Math.min((weekly.weeklyConnections / MAX_CONNECTIONS) * 100, 100);

  const handlePostsClick = () => {
    navi(`/profile/${user.userid}`);
  };

  const handleConnectionsClick = () => {
    navi(`/connections`);
  };

  const handleSkillClick = (skill) => {
    console.log("View skill:", skill);
  };

  return (
    <aside className="sidebar-dark">
      {/* Profile Section */}
      <div className="sidebar-section profile-section">
        <img
          src={`${process.env.REACT_APP_FILE_URL}${profile.profilepic}`}
          alt={profile.name}
        />
        <h3>{profile.name}</h3>
        <span className="role">{profile.headline || "Member"}</span>
        
        <div className="profile-metrics">
          <div 
            className="metric-card posts-metric" 
            onClick={handlePostsClick}
            role="button"
            tabIndex={0}
            aria-label="View posts"
          >
            <strong>{profile.postCount || 0}</strong>
            <span>Posts</span>
            <ExternalLink size={14} className="hover-icon" />
          </div>
          <div 
            className="metric-card connections-metric" 
            onClick={handleConnectionsClick}
            role="button"
            tabIndex={0}
            aria-label="View connections"
          >
            <strong>{profile.connections || 0}</strong>
            <span>Connections</span>
            <ExternalLink size={14} className="hover-icon" />
          </div>
        </div>
      </div>

      {/* Skills Section */}
      <div className="sidebar-section">
        <h4>Skills</h4>
        <div className="skill-tags">
          {profile.skills?.length > 0 ? (
            profile.skills.map((skill, index) => {
              const skillName = typeof skill === "object" ? skill.name : skill;
              return (
                <span 
                  key={index} 
                  className="skill-tag"
                  onClick={() => handleSkillClick(skillName)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${skillName} skills`}
                >
                  {skillName}
                  <Link2 size={12} className="hover-icon" />
                </span>
              );
            })
          ) : (
            <span className="empty-state">No skills added</span>
          )}
        </div>
      </div>

      {/* Weekly Activity Section */}
      <div className="sidebar-section">
        <h4>Weekly Activity</h4>
        
        {/* Posts Progress */}
        <div className="progress-item">
          <div className="progress-label">
            <span>Posts This Week</span>
            <div className="progress-value">
              <strong>{weekly.weeklyPosts}</strong>
              <span className="progress-max">/{MAX_POSTS}</span>
            </div>
          </div>
          <div className="progress-bar">
            <span 
              style={{ width: `${postsPercentage}%` }}
              className="progress-fill"
            >
              <span className="progress-tooltip">
                {weekly.weeklyPosts} / {MAX_POSTS} posts
              </span>
            </span>
          </div>
        </div>

        {/* Connections Progress */}
        <div className="progress-item">
          <div className="progress-label">
            <span>Connections This Week</span>
            <div className="progress-value">
              <strong>{weekly.weeklyConnections}</strong>
              <span className="progress-max">/{MAX_CONNECTIONS}</span>
            </div>
          </div>
          <div className="progress-bar">
            <span 
              style={{ width: `${connectionsPercentage}%` }}
              className="progress-fill"
            >
              <span className="progress-tooltip">
                {weekly.weeklyConnections} / {MAX_CONNECTIONS} connections
              </span>
            </span>
          </div>
        </div>

        {/* Weekly Summary */}
        <div className="weekly-summary">
          {weekly.weeklyPosts >= MAX_POSTS && weekly.weeklyConnections >= MAX_CONNECTIONS ? (
            <span className="summary-badge achieved">🎉 Amazing! You've hit 50/50 this week!</span>
          ) : weekly.weeklyPosts >= MAX_POSTS ? (
            <span className="summary-badge posts">📝 50 posts achieved! Great work!</span>
          ) : weekly.weeklyConnections >= MAX_CONNECTIONS ? (
            <span className="summary-badge connections">🤝 50 connections achieved! Awesome!</span>
          ) : (
            <span className="summary-badge">
              {MAX_POSTS - weekly.weeklyPosts} posts & {MAX_CONNECTIONS - weekly.weeklyConnections} connections to reach 50!
            </span>
          )}
        </div>
      </div>
    </aside>
  );
}